import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { Stack, TextField, Alert, Box, Typography, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { createTenant } from '../../../services/api/userService';

// ----------------------------------------------------------------------

export default function RegisterForm({ credentials }) {
	const { resendConfirmationCode, confirmSignup, login } = useAuth();
	const isMountedRef = useIsMountedRef();
	const { enqueueSnackbar } = useSnackbar();

	const ValidationSchema = Yup.object().shape({
		code: Yup.string().required('Verification code is required')
	});

	const formik = useFormik({
		initialValues: {
			code: ''
		},
		validationSchema: ValidationSchema,
		onSubmit: async (values, { setErrors, setSubmitting }) => {
			try {
				const response = await confirmSignup(values.code, credentials.email);

				if (response === 'SUCCESS') {
					enqueueSnackbar('Email confirmed!', {
						variant: 'success'
					});

					try {
						await login(credentials.email, credentials.password);
						await createTenant();
					} catch (error) {
						enqueueSnackbar(error.message, { variant: 'error' });
					}
				}

				if (isMountedRef.current) {
					setSubmitting(false);
				}
			} catch (error) {
				console.error(error);
				setErrors({ afterSubmit: error.message });
				setSubmitting(false);
			}
		}
	});

	const handleResendVerificationCodeClick = async () => {
		try {
			await resendConfirmationCode(credentials.email);
			enqueueSnackbar('Verification code sent', { variant: 'success' });
		} catch (error) {
			console.error(error);
			enqueueSnackbar(error.message, { variant: 'error' });
		}
	};

	const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

	return (
		<FormikProvider value={formik}>
			<Form style={{ width: '100%' }} autoComplete="off" noValidate onSubmit={handleSubmit}>
				<Stack spacing={3}>
					{errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

					<TextField
						fullWidth
						type="text"
						label="Verification code"
						{...getFieldProps('code')}
						error={Boolean(touched.code && errors.code)}
						helperText={touched.code && errors.code}
					/>

					<LoadingButton
						fullWidth
						size="large"
						type="submit"
						variant="contained"
						loading={isSubmitting}
					>
						Verify
					</LoadingButton>
					<Box>
						<Typography variant="body2">
							Didn't get the code?{' '}
							<Button onClick={handleResendVerificationCodeClick}>Resend</Button>
						</Typography>
					</Box>
				</Stack>
			</Form>
		</FormikProvider>
	);
}
